import { useState } from "react";
import { Feather, Loader2, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { authResetPassword } from "@/lib/api";
import { authBackgroundStyle } from "@/lib/authBackground";


function passwordStrength(pw) {
    if (!pw) return { score: 0, label: "", color: "" };
    let score = 0;
    if (pw.length >= 8) score++;
    if (pw.length >= 12) score++;
    if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
    if (/\d/.test(pw)) score++;
    if (/[^A-Za-z0-9]/.test(pw)) score++;
    const buckets = [
        { label: "Too weak", color: "bg-red-400" },
        { label: "Weak", color: "bg-orange-400" },
        { label: "Fair", color: "bg-yellow-400" },
        { label: "Good", color: "bg-lime-400" },
        { label: "Strong", color: "bg-emerald-400" },
    ];
    const idx = Math.min(score, 4);
    return { score: idx, ...buckets[idx] };
}

export default function ResetPassword() {
    const token = new URLSearchParams(window.location.search).get("token") || "";
    const [password, setPassword] = useState("");
    const [confirm, setConfirm] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);
    const strength = passwordStrength(password);

    async function submit(e) {
        e.preventDefault();
        setError("");
        if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
        if (password !== confirm) { setError("Passwords do not match."); return; }
        setBusy(true);
        try {
            await authResetPassword(token, password);
            setDone(true);
        } catch (err) {
            const d = err.response?.data?.detail;
            setError(typeof d === "string" ? d : "This reset link is invalid or has expired.");
            setBusy(false);
        }
    }

    return (
        <div className="min-h-screen relative text-white flex items-center justify-center p-6"
            style={authBackgroundStyle}>
            <div className="absolute inset-0 bg-black/60" />
            <div className="relative z-10 w-full max-w-md bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-10">
                <div className="flex items-center justify-center gap-2 mb-6">
                    <Feather className="w-5 h-5 text-white/90" />
                    <span className="font-heading text-2xl tracking-tight">Day Dreamer</span>
                </div>
                {done ? (
                    <div data-testid="reset-success" className="text-center space-y-4">
                        <CheckCircle2 className="w-10 h-10 mx-auto text-emerald-300" />
                        <h1 className="font-heading text-2xl">Password reset</h1>
                        <p className="text-white/80">You can now sign in with your new password.</p>
                        <Button data-testid="reset-go-signin" onClick={() => { window.location.href = "/"; }}
                            className="rounded-full h-11 px-7 bg-white text-foreground hover:bg-white/90">
                            Go to sign in
                        </Button>
                    </div>
                ) : !token ? (
                    <div data-testid="reset-no-token" className="text-center space-y-4">
                        <h1 className="font-heading text-2xl">Invalid link</h1>
                        <p className="text-white/80">This reset link is missing its token. Request a new one from the sign-in page.</p>
                        <Button onClick={() => { window.location.href = "/"; }}
                            className="rounded-full h-11 px-7 bg-white text-foreground hover:bg-white/90">Back to sign in</Button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
                        <h1 className="font-heading text-2xl text-center">Choose a new password</h1>
                        <div className="relative">
                            <Input data-testid="reset-password-input" type={showPw ? "text" : "password"} required value={password}
                                onChange={(e) => setPassword(e.target.value)} placeholder="New password (min 8 characters)"
                                autoComplete="new-password"
                                className="h-11 pr-11 bg-white/95 text-foreground placeholder:text-muted-foreground border-0" />
                            <button type="button" data-testid="reset-toggle-password" onClick={() => setShowPw((v) => !v)}
                                aria-label={showPw ? "Hide password" : "Show password"}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
                                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                            </button>
                        </div>
                        {password && (
                            <div data-testid="reset-password-strength" className="space-y-1">
                                <div className="flex gap-1">
                                    {[0, 1, 2, 3, 4].map((i) => (
                                        <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${i <= strength.score ? strength.color : "bg-white/20"}`} />
                                    ))}
                                </div>
                                <p className="text-xs text-white/70">Password strength: <span className="text-white/90">{strength.label}</span></p>
                            </div>
                        )}
                        <Input data-testid="reset-confirm-input" type={showPw ? "text" : "password"} required value={confirm}
                            onChange={(e) => setConfirm(e.target.value)} placeholder="Confirm new password"
                            autoComplete="new-password"
                            className="h-11 bg-white/95 text-foreground placeholder:text-muted-foreground border-0" />
                        {error && <p data-testid="reset-error" className="text-sm text-red-300">{error}</p>}
                        <Button data-testid="reset-submit" type="submit" disabled={busy}
                            className="w-full rounded-full h-12 text-base bg-white text-foreground hover:bg-white/90">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                            Reset password
                        </Button>
                    </form>
                )}
            </div>
        </div>
    );
}
